import { identityApiRef, useApi } from '@backstage/core-plugin-api';

import { scmAuthApiRef } from '@backstage/integration-react';
import { useMemo, useState } from 'react';

import useDebounce from 'react-use/lib/useDebounce';
import {
  FieldExtensionComponentProps,
  OwnedEntityPickerUiOptions,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder';
import {
  DevHubAutocomplete,
  DevHubFormControl,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  ANNOTATION_SOURCE_LOCATION,
  Entity,
  RELATION_OWNED_BY,
} from '@backstage/catalog-model';

import { GetEntitiesResponse } from '@backstage/catalog-client';

import { parseSourceUrl } from '../../util/entityUtils';

type RequestUserCredentialsOptions = {
  secretsKey: string;
  enabled: boolean;
};

/**
 * The input properties for {@link ExistingEntityPicker}
 */
export type ExistingEntityPickerProps = FieldExtensionComponentProps<
  Entity | undefined,
  OwnedEntityPickerUiOptions
>;

/**
 * This component allows users to select an existing Component entity for use in templating.
 *
 * It will return the selected {@link Entity} for use in the processing steps.
 *
 * @param props - The properties passed to the component.
 */
export const ExistingEntityPicker = (props: ExistingEntityPickerProps) => {
  const {
    onChange,
    schema: { title = 'Entity', description = 'An entity from the catalog' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
  } = props;

  const uiOptions = uiSchema['ui:options'];
  const allowedKinds = uiOptions?.allowedKinds ?? ['Component'];
  const allowArbitraryValues = uiOptions?.allowArbitraryValues ?? false;
  const requestUserCredentials: RequestUserCredentialsOptions =
    (uiOptions?.requestUserCredentials ?? {
      secretsKey: 'USER_OAUTH_TOKEN',
      enabled: true,
    }) as RequestUserCredentialsOptions;

  const { ownedEntities, loading } = useOwnedEntities(allowedKinds);
  const scmAuthApi = useApi(scmAuthApiRef);
  const { setSecrets } = useTemplateSecrets();

  function entityName(e: Entity) {
    if (!e) {
      return undefined;
    }
    return e?.metadata?.title ?? e?.metadata?.name ?? undefined;
  }

  // Get the owned entity refs
  const entityRefs = ownedEntities?.items
    .map(e => entityName(e))
    .filter(n => n);

  // Create a lookup map ref -> entity
  const entityLookup =
    ownedEntities?.items.reduce((map, entity) => {
      map.set(entityName(entity) ?? 'unknown', entity);
      return map;
    }, new Map<string, Entity>()) ?? new Map<string, Entity>();

  // Default to undefined if not a valid entity
  let initialState = formData;
  if (!initialState?.metadata) {
    initialState = undefined;
  }

  // Establish our state to track the selected entity
  const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(
    initialState,
  );

  // Update the current entity onSelect
  const onSelect = (_: any, value: string | null | undefined) => {
    const locatedEntity = entityLookup.get(value ?? '');
    setSelectedEntity(locatedEntity || undefined);
    onChange(locatedEntity || undefined);
  };

  useDebounce(
    async () => {
      if (
        !selectedEntity ||
        !ownedEntities ||
        !requestUserCredentials ||
        !requestUserCredentials?.enabled
      ) {
        return;
      }

      const sourceLocation =
        selectedEntity?.metadata?.annotations?.[ANNOTATION_SOURCE_LOCATION];
      if (!sourceLocation) {
        throw new Error('Entity missing source location.');
      }
      const parsed = parseSourceUrl(sourceLocation);
      const [host, owner, repo] = [parsed.host, parsed.owner, parsed.repo].map(
        encodeURIComponent,
      );

      // We are expected to use user credentials, so let's get them
      const { token } = await scmAuthApi.getCredentials({
        url: `https://${host}/${owner}/${repo}`,
        additionalScope: {
          repoWrite: true,
        },
      });

      // Sore the token as a secret
      setSecrets({ [requestUserCredentials.secretsKey]: token });
    },
    500,
    [selectedEntity, uiSchema],
  );

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !selectedEntity}
    >
      <DevHubAutocomplete
        id={idSchema?.$id}
        value={entityName(selectedEntity ?? ({} as Entity)) ?? ''}
        loading={loading}
        onChange={onSelect}
        options={entityRefs || []}
        autoSelect
        freeSolo={allowArbitraryValues}
        renderInput={params => (
          <DevHubTextField
            {...params}
            label={title}
            margin="normal"
            helperText={description}
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
          />
        )}
      />
    </DevHubFormControl>
  );
};

/**
 * Takes the relevant parts of the Backstage identity, and translates them into
 * a list of entities which are owned by the user. Takes an optional parameter
 * to filter the entities based on allowedKinds
 *
 *
 * @param allowedKinds - Array of allowed kinds to filter the entities
 */
function useOwnedEntities(allowedKinds?: string[]): {
  loading: boolean;
  ownedEntities: GetEntitiesResponse | undefined;
} {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { loading, value: refs } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const identityRefs = identity.ownershipEntityRefs;
    const catalogs = await catalogApi.getEntities(
      allowedKinds
        ? {
            filter: {
              kind: allowedKinds,
              [`relations.${RELATION_OWNED_BY}`]: identityRefs || [],
            },
          }
        : {
            filter: {
              [`relations.${RELATION_OWNED_BY}`]: identityRefs || [],
            },
          },
    );
    return catalogs;
  }, []);

  const ownedEntities = useMemo(() => {
    return refs;
  }, [refs]);

  return useMemo(() => ({ loading, ownedEntities }), [loading, ownedEntities]);
}
